import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ConfirmPassword.css";
import axiosInstance from "../axiosinstance";

const ConfirmPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email =
    location.state?.email ||
    sessionStorage.getItem("reset_email") ||
    sessionStorage.getItem("otp_email") ||
    "";
  const resetToken =
    location.state?.resetToken || sessionStorage.getItem("reset_token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({
    text: "",
    className: "",
    percentage: 0,
  });
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const getBackendMessage = (payloadOrError) => {
    const data = payloadOrError?.response?.data || payloadOrError;
    if (!data) return "";
    if (typeof data === "string") return data;
    return data.message || data.error || "";
  };

  useEffect(() => {
    if (location.state?.email) {
      sessionStorage.setItem("reset_email", location.state.email);
    }
    if (location.state?.resetToken) {
      sessionStorage.setItem("reset_token", location.state.resetToken);
    }

    if (!email || !resetToken) {
      setAlertMessage(
        "Reset session expired or invalid. Please restart the password reset process."
      );
      setTimeout(() => navigate("/forgot-password", { replace: true }), 1500);
    }
  }, [email, resetToken, navigate, location.state]);

  // Password Strength Validation
  const validatePassword = (value) => {
    const minLength = /.{8,}/; // At least 8 characters
    const upperCase = /[A-Z]/; // At least one uppercase letter
    const number = /[0-9]/; // At least one number
    const specialChar = /[!@#$%^&*(),.?":{}|<>]/; // At least one special character

    let strength = 0;
    if (minLength.test(value)) strength += 1;
    if (upperCase.test(value)) strength += 1;
    if (number.test(value)) strength += 1;
    if (specialChar.test(value)) strength += 1;

    if (strength === 0)
      return { text: "Weak ❌", className: "weak", percentage: 25 };
    if (strength === 1)
      return { text: "Weak 🔴", className: "weak", percentage: 25 };
    if (strength === 2)
      return { text: "Fair 🟠", className: "fair", percentage: 50 };
    if (strength === 3)
      return { text: "Good 🟡", className: "good", percentage: 75 };
    if (strength === 4)
      return { text: "Strong ✅", className: "strong", percentage: 100 };

    return { text: "", className: "", percentage: 0 };
  };

  // Handle new password input
  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordStrength(validatePassword(value)); // Update strength meter
  };

  // Handle form submission
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAlertMessage(""); // Clear any previous error message

    if (!email || !resetToken) {
      setAlertMessage("Reset session missing. Please restart from forgot password.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setAlertMessage("Please fill out both fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setAlertMessage("Passwords do not match.");
      return;
    }

    if (
      passwordStrength.className === "weak" ||
      passwordStrength.className === "fair"
    ) {
      setAlertMessage("Please choose a stronger password.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        new_password: newPassword,
        email: email,
        reset_token: resetToken,
      };

      const res = await axiosInstance.post("/api/reset_password", payload);
      const responseData = res?.data || {};
      const resetSuccess = responseData?.success === true;

      if (resetSuccess) {
        sessionStorage.removeItem("otp_email");
        sessionStorage.removeItem("reset_email");
        sessionStorage.removeItem("reset_token");
        setAlertMessage(
          responseData?.message || "Password reset successful!"
        );
        setTimeout(() => navigate("/login"), 2000);
      } else {
        sessionStorage.removeItem("reset_token");
        setAlertMessage(
          getBackendMessage(responseData) ||
            "Failed to reset password. Please try again."
        );
      }
    } catch (error) {
      const backendMessage = getBackendMessage(error).toLowerCase();

      if (backendMessage.includes("token") && backendMessage.includes("expired")) {
        setAlertMessage("Reset session expired. Please verify OTP again.");
        sessionStorage.removeItem("reset_token");
      } else if (
        backendMessage.includes("invalid") &&
        backendMessage.includes("token")
      ) {
        setAlertMessage("Invalid reset session. Please verify OTP again.");
        sessionStorage.removeItem("reset_token");
      } else if (backendMessage) {
        setAlertMessage(getBackendMessage(error));
      } else {
        setAlertMessage("A server error occurred. Please try again later.");
      }
      if (error?.response) {
        sessionStorage.removeItem("reset_token");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="confirm-container">
      <h2 className="confirm-title">Confirm New Password</h2>
      {alertMessage && <div className="confirm-alert">{alertMessage}</div>}

      <form onSubmit={handlePasswordReset} className="confirm-form">
        <label htmlFor="new-password" className="confirm-label">
          New Password:
        </label>
        <input
          type="password"
          id="new-password"
          className="confirm-input"
          placeholder="Enter new password"
          value={newPassword}
          onChange={handleNewPasswordChange}
        />

        {/* Password Strength Meter */}
        {newPassword && (
          <div className="password-strength-container">
            <div className="password-strength-bar">
              <div
                className={`strength-meter ${passwordStrength.className}`}
                style={{ width: `${passwordStrength.percentage}%` }}
              ></div>
            </div>
            <p
              className={`password-strength-text ${passwordStrength.className}-text`}
            >
              {passwordStrength.text}
            </p>
          </div>
        )}

        <label htmlFor="confirm-password" className="confirm-label">
          Confirm Password:
        </label>
        <input
          type="password"
          id="confirm-password"
          className="confirm-input"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />

        <button type="submit" disabled={loading} className="confirm-button">
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </form>
    </div>
  );
};

export default ConfirmPassword;
